import React, { useState, useRef, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import '../CompanyForm/style.css';
import './style.css';

// Helper to format date as YYYY-MM-DD using local time
export const formatDateToISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDateParams = (mode, date) => {
  // Garantir que trabalhamos com um objeto Date e evitamos mutações
  let refDate;
  if (date instanceof Date) {
    refDate = new Date(date.getTime());
  } else if (typeof date === 'string') {
    // Se for string YYYY-MM-DD, extraímos as partes para evitar shift de fuso horário
    const parts = date.split('T')[0].split('-');
    if (parts.length === 3) {
      refDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    } else {
      refDate = new Date(date);
    }
  } else {
    refDate = new Date();
  }

  const start = new Date(refDate.getTime());
  const end = new Date(refDate.getTime());
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  switch (mode) {
    case 'daily':
      return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
    case 'weekly':
      // Alinha para o domingo da semana atual
      start.setDate(refDate.getDate() - refDate.getDay());
      end.setDate(start.getDate() + 6);
      return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
    case 'monthly':
      start.setDate(1);
      end.setMonth(start.getMonth() + 1);
      end.setDate(0);
      return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
    case 'annual':
      start.setMonth(0, 1);
      end.setMonth(11, 31);
      return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
    case 'last12':
      start.setFullYear(start.getFullYear() - 1);
      return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
    case 'yesterday':
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterday.getTime());
      yesterdayEnd.setHours(23, 59, 59, 999);
      return { startDate: formatDateToISO(yesterday), endDate: formatDateToISO(yesterdayEnd) };
    case 'all':
      return { startDate: null, endDate: null };
    case 'custom': {
      if (date && typeof date === 'object' && !(date instanceof Date) && date.startDate && date.endDate) {
        return { startDate: date.startDate, endDate: date.endDate };
      }
      return { startDate: null, endDate: null };
    }
    default:
      return { startDate: null, endDate: null };
  }
};

export default function DatePeriodFilter({
  periodMode,
  setPeriodMode,
  currentDate,
  setCurrentDate,
  customRange,
  setCustomRange,
  label = "Período",
  forceHeight,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const containerRef = useRef(null);
  const isClosingByBack = useRef(false);

  const parseISODateToLocalDate = (value) => {
    if (!value) return null;
    const parts = String(value).split('T')[0].split('-');
    if (parts.length !== 3) return null;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const openCustomModal = () => {
    const fallback = getDateParams(periodMode, currentDate);
    const startDate = customRange?.startDate || fallback.startDate || formatDateToISO(new Date());
    const endDate = customRange?.endDate || fallback.endDate || formatDateToISO(new Date());
    setDraftStartDate(startDate || '');
    setDraftEndDate(endDate || '');
    setIsCustomModalOpen(true);
  };

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ datePeriodFilterOpen: 'date-period-filter' }, '');
      
      const handlePopState = () => {
        isClosingByBack.current = true;
        setIsOpen(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        const currentState = window.history.state;
        if (!isClosingByBack.current && currentState && currentState.datePeriodFilterOpen === 'date-period-filter') {
          window.history.back();
        }
        isClosingByBack.current = false;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        document.body.setAttribute('data-dropdown-closed', 'true');
        setTimeout(() => {
          document.body.removeAttribute('data-dropdown-closed');
        }, 200);
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handlePeriodSelect = (mode) => {
    if (mode === 'custom') {
      setIsOpen(false);
      openCustomModal();
      return;
    }

    setPeriodMode(mode);
    setIsOpen(false);
    
    // Logic from FinancialOverview: reset date for certain modes
    if (mode === 'daily' || mode === 'weekly' || mode === 'last12') {
      const now = new Date();
      setCurrentDate(now);
    } else if (mode === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      // We don't set currentDate to yesterday here because it might confuse other components expecting 'currentDate' to be the reference point
      // But for 'yesterday' mode, the getDateParams function handles the calculation internally
      // However, if we want consistency in display, maybe we should set it.
      // Let's set it to yesterday so the UI might reflect it if it uses currentDate for display
      setCurrentDate(yesterday);
    }
  };

  const handlePrevPeriod = (e) => {
    e.stopPropagation(); // Prevent opening menu if button is inside clickable area (though it's not in structure below)
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (periodMode === 'monthly') {
        newDate.setDate(1);
        newDate.setMonth(prev.getMonth() - 1);
      }
      else if (periodMode === 'annual') newDate.setFullYear(prev.getFullYear() - 1);
      return newDate;
    });
  };

  const handleNextPeriod = (e) => {
    e.stopPropagation();
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (periodMode === 'monthly') {
        newDate.setDate(1);
        newDate.setMonth(prev.getMonth() + 1);
      }
      else if (periodMode === 'annual') newDate.setFullYear(prev.getFullYear() + 1);
      return newDate;
    });
  };

  const getPeriodLabel = () => {
    if (!currentDate) return '';
    
    const formattedMonthYear = currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    const capitalizedMonthYear = formattedMonthYear.charAt(0).toUpperCase() + formattedMonthYear.slice(1);

    switch (periodMode) {
      case 'daily': return `Hoje (${new Date().toLocaleDateString('pt-BR')})`;
      case 'yesterday': return `Ontem (${currentDate ? currentDate.toLocaleDateString('pt-BR') : ''})`;
      case 'weekly': return 'Esta Semana';
      case 'monthly': return capitalizedMonthYear;
      case 'annual': return currentDate.getFullYear().toString();
      case 'last12': return 'Últimos 12 Meses';
      case 'all': return 'Todo o Período';
      case 'custom': {
        if (customRange?.startDate && customRange?.endDate) {
          const start = parseISODateToLocalDate(customRange.startDate);
          const end = parseISODateToLocalDate(customRange.endDate);
          const startLabel = start ? start.toLocaleDateString('pt-BR') : customRange.startDate;
          const endLabel = end ? end.toLocaleDateString('pt-BR') : customRange.endDate;
          return `Personalizado (${startLabel} - ${endLabel})`;
        }
        return 'Personalizado';
      }
      default: return capitalizedMonthYear;
    }
  };

  const isNavigable = periodMode === 'monthly' || periodMode === 'annual';

  const periodOptions = [
    { id: 'yesterday', label: 'Ontem' },
    { id: 'daily', label: 'Hoje' },
    { id: 'weekly', label: 'Semanal' },
    { id: 'monthly', label: 'Mensal' },
    { id: 'annual', label: 'Anual' },
    { id: 'last12', label: 'Últimos 12 meses' },
    { id: 'custom', label: 'Personalizado' },
    { id: 'all', label: 'Todo o período' }
  ];

  return (
    <>
      <div className="date-period-filter-wrapper" ref={containerRef} style={{height: forceHeight}}>
        {label && <label className="date-period-label">{label}</label>}
      
        <div className="date-period-input">
          {isNavigable ? (
            <button 
              type="button" 
              className="date-period-nav-button"
              onClick={handlePrevPeriod}
            >
              <FiChevronLeft size={20} />
            </button>
          ) : (
            <div className="date-period-nav-spacer" />
          )}

          <div 
            className="date-period-value"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>{getPeriodLabel()}</span>
            <FiChevronDown 
              size={16} 
              style={{ 
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s'
              }} 
            />
          </div>

          {isNavigable ? (
            <button 
              type="button" 
              className="date-period-nav-button"
              onClick={handleNextPeriod}
            >
              <FiChevronRight size={20} />
            </button>
          ) : (
            <div className="date-period-nav-spacer" />
          )}
        </div>

        {isOpen && (
          <div className="date-period-menu">
            {periodOptions.map((option) => (
              <div 
                key={option.id}
                className={`date-period-menu-item ${periodMode === option.id ? 'active' : ''}`}
                onClick={() => handlePeriodSelect(option.id)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        title="Período Personalizado"
        closeOnOverlayClick={false}
      >
        <div className="company-form-component">
          <div className="form-body">
            <div className="form-grid">
              <Input
                id="custom-start-date"
                label="Data inicial"
                type="date"
                value={draftStartDate}
                onChange={(e) => setDraftStartDate(e.target.value)}
              />
              <Input
                id="custom-end-date"
                label="Data final"
                type="date"
                value={draftEndDate}
                onChange={(e) => setDraftEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-actions">
            <Button type="button" className="cancel-button" onClick={() => setIsCustomModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!draftStartDate || !draftEndDate) return;
                if (draftStartDate > draftEndDate) return;
                if (setCustomRange) {
                  setCustomRange({ startDate: draftStartDate, endDate: draftEndDate });
                }
                setPeriodMode('custom');
                const start = parseISODateToLocalDate(draftStartDate);
                if (start) setCurrentDate(start);
                setIsCustomModalOpen(false);
              }}
              disabled={!draftStartDate || !draftEndDate || draftStartDate > draftEndDate}
            >
              Aplicar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
